import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Dialog,
  DialogContent,
  Box, 
  IconButton, 
  Typography,
  useTheme,
  Avatar,
  Button,
  Fade,
  LinearProgress
} from '@mui/material';
import { Close, Notifications } from '@mui/icons-material';

// Toast Types
export interface Toast {
  id: string;
  title?: string;
  message: string;
  contactNo?: string;
  email?: string;
  duration?: number;
  action?: React.ReactNode;
  persistent?: boolean;
}

export interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

// Toast Context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Custom Hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast Dialog Component
interface ToastDialogProps {
  toast: Toast;
  onRemove: (id: string) => void;
  isVisible: boolean;
  stackIndex: number;
}

const ToastDialog: React.FC<ToastDialogProps> = ({ toast, onRemove, isVisible, stackIndex }) => {
  const theme = useTheme();
  const [timeLeft, setTimeLeft] = useState(toast.duration || 5000);
  const [progress, setProgress] = useState(100);
  
  React.useEffect(() => {
    if (!toast.persistent && toast.duration !== 0 && isVisible) {
      const duration = toast.duration || 5000;
      const interval = 100; // Update every 100ms for smooth progress
      
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          const newTimeLeft = prev - interval;
          setProgress((newTimeLeft / duration) * 100);
          
          if (newTimeLeft <= 0) {
            clearInterval(timer);
            onRemove(toast.id);
            return 0;
          }
          return newTimeLeft;
        });
      }, interval);
      
      return () => clearInterval(timer);
    }
  }, [toast.id, toast.duration, toast.persistent, onRemove, isVisible]);

  const formatTime = (milliseconds: number) => {
    const seconds = Math.ceil(milliseconds / 1000);
    return `${seconds}s`;
  };

  return (
    <Dialog
      open={isVisible}
      onClose={() => onRemove(toast.id)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10],
          transform: `translateY(${stackIndex * 10}px) scale(${1 - stackIndex * 0.05})`,
          zIndex: 1300 - stackIndex,
          position: 'relative',
          overflow: 'visible',
          backgroundColor: stackIndex > 0 ? 'rgba(255, 255, 255, 0.95)' : '#fff',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Progress Bar */}
        {!toast.persistent && toast.duration !== 0 && isVisible && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              borderRadius: '12px 12px 0 0',
              backgroundColor: 'rgba(0, 96, 174, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#0060AE',
                borderRadius: '12px 12px 0 0',
              },
            }}
          />
        )}

        {/* Close Button */}
        <IconButton
          onClick={() => onRemove(toast.id)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        {/* Timer Display */}
        {!toast.persistent && toast.duration !== 0 && isVisible && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'rgba(0, 96, 174, 0.1)',
              borderRadius: 2,
              px: 1,
              py: 0.5,
              zIndex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#0060AE',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: 3, textAlign: 'center', pt: toast.persistent || toast.duration === 0 ? 3 : 4 }}>
          {/* Notification Icon with Glow Effect */}
          <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255, 82, 82, 0.3) 0%, rgba(255, 82, 82, 0.1) 70%, transparent 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255, 82, 82, 0.2) 0%, transparent 70%)',
                  animation: 'pulse 2s infinite',
                },
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.7,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }}
            >
              <Avatar
                sx={{
                  backgroundColor: '#ff5252',
                  width: 50,
                  height: 50,
                  boxShadow: '0 4px 20px rgba(255, 82, 82, 0.4)',
                }}
              >
                <Notifications sx={{ color: '#fff', fontSize: 28 }} />
              </Avatar>
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: '#666',
              fontSize: '1rem',
            }}
          >
            {toast.title || 'New Notification'}
          </Typography>

          {/* Message */}
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: '#333',
              fontSize: '1.2rem',
              lineHeight: 1.5,
            }}
          >
            {toast.message}
          </Typography>

          {/* Contact Information */}
          {toast.contactNo && (
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                color: '#444',
                fontWeight: 500,
              }}
            >
              Contact No : {toast.contactNo}
            </Typography>
          )}

          {toast.email && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: '#444',
                fontWeight: 500,
              }}
            >
              Email : {toast.email}
            </Typography>
          )}

          {/* Dismiss Text */}
          <Typography
            variant="caption"
            sx={{
              color: '#999',
              fontSize: '0.8rem',
              mb: 2,
              display: 'block',
            }}
          >
            {toast.persistent || toast.duration === 0 
              ? 'Click "Got it!" to dismiss this notification'
              : `Auto-dismiss in ${formatTime(timeLeft)} or click "Got it!" to dismiss`
            }
          </Typography>

          {/* Action Button */}
          <Box sx={{ mt: 2 }}>
            {toast.action || (
              <Button
                variant="contained"
                onClick={() => onRemove(toast.id)}
                sx={{
                  backgroundColor: '#0060AE',
                  color: '#fff',
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#004d8a',
                  },
                }}
              >
                GOT IT!
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Toast Container Component
const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <>
      {toasts.map((toast, index) => (
        <ToastDialog
          key={toast.id}
          toast={toast}
          onRemove={removeToast}
          isVisible={index === 0} // Only show the first toast
          stackIndex={index}
        />
      ))}
    </>
  );
};

// Toast Provider Component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const toast: Toast = {
      id,
      duration: 5000, // Default 5 seconds
      ...toastData,
    };

    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

export const useToastHelpers = () => {
  const { addToast } = useToast();

  return {
    showNotification: (
      message: string, 
      options?: Partial<Omit<Toast, 'id' | 'message'>>
    ) => addToast({ message, ...options }),
  };
};

export default ToastProvider;
